import { History, MapPin, ShieldAlert } from "lucide-react";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}
function fmtDuration(min) {
  if (min == null) return "—";
  const h = Math.floor(min / 60);
  const m = Math.round(min - h * 60);
  return h ? `${h}h ${m}m` : `${m}m`;
}

function ColoredBadge({ color, label }) {
  return (
    <span
      className="inline-block px-2.5 py-1 rounded-sm border text-xs font-bold"
      style={color ? { background: color.bg, color: color.text, borderColor: color.border } : {}}
    >
      {label}
    </span>
  );
}

function LocationCell({ event }) {
  const lat = event.clock_in_lat;
  const lng = event.clock_in_lng;
  const dist = event.clock_in_distance_m;
  if (lat == null || lng == null) return <span className="text-gray-300">—</span>;
  const url = `https://www.google.com/maps?q=${lat},${lng}`;
  const distLabel = dist == null ? "" : `${Math.round(dist)} m`;
  const outOfFence = dist != null && dist > 50;
  return (
    <div className="flex items-center gap-2 text-xs">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#002FA7] hover:underline flex items-center gap-1"
        title={`${lat.toFixed(5)}, ${lng.toFixed(5)}`}
        data-testid={`attendance-pin-${event.id}`}
      >
        <MapPin className="w-3.5 h-3.5" />
        Map
      </a>
      <span className={`font-mono ${outOfFence ? "text-red-700 font-bold" : "text-gray-600"}`}>
        {distLabel}
      </span>
      {event.override && (
        <span
          className="px-1.5 py-0.5 bg-amber-100 text-amber-900 border border-amber-200 text-[9px] font-bold uppercase tracking-widest rounded-sm"
          title={event.override_approver_name ? `Approved by ${event.override_approver_name}` : "Admin-approved override"}
        >
          override{event.override_approver_name ? ` · ${event.override_approver_name}` : ""}
        </span>
      )}
      {event.left_geofence_at && (
        <span
          title={`Left geofence at ${fmtDateTime(event.left_geofence_at)}`}
          className="flex items-center gap-0.5 px-1.5 py-0.5 bg-red-100 text-red-800 border border-red-200 text-[9px] font-bold uppercase tracking-widest rounded-sm"
        >
          <ShieldAlert className="w-3 h-3" /> left
        </span>
      )}
      {event.left_without_return && (
        <span
          title="Left the geofence and did not return before clocking out"
          className="px-1.5 py-0.5 bg-red-600 text-white border border-red-700 text-[9px] font-bold uppercase tracking-widest rounded-sm"
        >
          left-without-return
        </span>
      )}
      {event.returned_to_geofence_at && (
        <span
          title={`Returned at ${fmtDateTime(event.returned_to_geofence_at)}`}
          className="px-1.5 py-0.5 bg-green-100 text-green-900 border border-green-200 text-[9px] font-bold uppercase tracking-widest rounded-sm"
        >
          returned
        </span>
      )}
    </div>
  );
}

/**
 * Presentational attendance log table. All filtering / data fetching happens
 * in the parent (AttendanceLog).
 */
export default function AttendanceTable({ events, loading }) {
  return (
    <div className="border border-gray-300 bg-white rounded-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <History className="w-5 h-5 text-[#002FA7]" />
          <h3 className="font-black text-lg tracking-tight">Team member attendance log</h3>
        </div>
        {loading && <span className="text-xs text-gray-400">Loading…</span>}
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100 text-[10px] uppercase tracking-widest text-gray-500">
            <tr>
              <th className="text-left px-4 py-2.5">Team Member</th>
              <th className="text-left px-4 py-2.5">Site</th>
              <th className="text-left px-4 py-2.5">Logged in</th>
              <th className="text-left px-4 py-2.5">Logged out</th>
              <th className="text-left px-4 py-2.5">Duration</th>
              <th className="text-left px-4 py-2.5">Location</th>
              <th className="text-left px-4 py-2.5">Status</th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 && !loading && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-400 italic">
                  No attendance records yet.
                </td>
              </tr>
            )}
            {events.map((e) => (
              <tr key={e.id} className="border-t border-gray-100" data-testid={`attendance-row-${e.id}`}>
                <td className="px-4 py-2"><ColoredBadge color={e.worker_color} label={e.worker_name} /></td>
                <td className="px-4 py-2"><ColoredBadge color={e.site_color} label={e.site_name} /></td>
                <td className="px-4 py-2 font-mono text-xs">{fmtDateTime(e.clock_in)}</td>
                <td className="px-4 py-2 font-mono text-xs">
                  {e.clock_out ? fmtDateTime(e.clock_out) : <span className="text-green-700 font-bold">— OPEN —</span>}
                </td>
                <td className="px-4 py-2 font-mono text-xs">{fmtDuration(e.duration_minutes)}</td>
                <td className="px-4 py-2"><LocationCell event={e} /></td>
                <td className="px-4 py-2">
                  <span
                    className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-sm ${
                      e.status === "OPEN" ? "bg-green-600 text-white" : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {e.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
